/**
 * WorkflowsPage.refactored.tsx
 * Refactored version of the uploaded Workflow page.
 * - Preserves original logic and data as requested (no behavior changes).
 * - Extracts UI into smaller local components (keeps single file to avoid import churn).
 * - Adds comments, improves spacing and layout to reduce visual congestion.
 * - Contains the same mock data and types (unchanged).
 *
 * NOTE: This refactor keeps all code in one file so you can paste it into VS Code
 * and it will work without having to add new files or change imports.
 */

import { useState, useEffect, type FC } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Play,
  Pause,
  GitBranch,
  Clock,
  CheckCircle,
  XCircle,
  Copy,
  Edit,
  Trash2,
  Eye,
  Upload,
  Share,
  Star,
  TrendingUp,
  Activity,
  BarChart3,
  Workflow,
  Database,
  Server,
  Shield,
  MoreVertical,
  Timer
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

// -----------------------------------------------------------------------------
// Types (unchanged)
// -----------------------------------------------------------------------------
interface WorkflowDefinition {
  id: string;
  name: string;
  description: string;
  version: string;
  status: 'draft' | 'published' | 'archived';
  category: 'data-processing' | 'infrastructure' | 'security' | 'deployment' | 'monitoring';
  complexity: 'simple' | 'medium' | 'complex';
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  tags: string[];
  stepCount: number;
  averageExecutionTime: number;
  successRate: number;
  usageCount: number;
  lastRun?: string;
  isPublic: boolean;
  rating: number;
}

interface WorkflowExecution {
  id: string;
  workflowId: string;
  workflowName: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled' | 'pending';
  startTime: string;
  endTime?: string;
  duration?: number;
  triggeredBy: string;
  stepsCompleted: number;
  totalSteps: number;
  errorMessage?: string;
  outputData?: any;
  resourcesUsed: number;
}

interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  complexity: string;
  tags: string[];
  usageCount: number;
  rating: number;
  preview: string;
  author: string;
  isPremium: boolean;
}

interface WorkflowMetrics {
  totalWorkflows: number;
  activeWorkflows: number;
  executionsToday: number;
  avgSuccessRate: number;
  totalExecutionTime: number;
  popularCategory: string;
}

// -----------------------------------------------------------------------------
// Mock data (unchanged from original) — left intact so logic remains the same.
// -----------------------------------------------------------------------------
const mockWorkflows: WorkflowDefinition[] = [
  {
    id: 'wf-def-001',
    name: 'CI/CD Pipeline Automation',
    description: 'Automated build, test, and deployment pipeline for microservices',
    version: '2.1.0',
    status: 'published',
    category: 'deployment',
    complexity: 'complex',
    createdAt: '2024-11-15T10:30:00Z',
    updatedAt: '2024-12-10T14:20:00Z',
    createdBy: 'DevOps Team',
    tags: ['ci-cd', 'kubernetes', 'docker', 'testing'],
    stepCount: 12,
    averageExecutionTime: 480,
    successRate: 94.2,
    usageCount: 156,
    lastRun: '2 hours ago',
    isPublic: true,
    rating: 4.8
  },
  {
    id: 'wf-def-002',
    name: 'Data Lake ETL Processing',
    description: 'Extract, transform, and load data from multiple sources to data lake',
    version: '1.3.2',
    status: 'published',
    category: 'data-processing',
    complexity: 'complex',
    createdAt: '2024-10-20T09:15:00Z',
    updatedAt: '2024-12-08T11:45:00Z',
    createdBy: 'Data Engineering',
    tags: ['etl', 'big-data', 'spark', 'aws-s3'],
    stepCount: 8,
    averageExecutionTime: 720,
    successRate: 98.5,
    usageCount: 89,
    lastRun: '6 hours ago',
    isPublic: false,
    rating: 4.9
  },
  {
    id: 'wf-def-003',
    name: 'Security Vulnerability Assessment',
    description: 'Automated security scanning and vulnerability reporting',
    version: '1.0.5',
    status: 'published',
    category: 'security',
    complexity: 'medium',
    createdAt: '2024-12-01T16:00:00Z',
    updatedAt: '2024-12-15T13:30:00Z',
    createdBy: 'Security Team',
    tags: ['security', 'vulnerability', 'compliance', 'scanning'],
    stepCount: 6,
    averageExecutionTime: 180,
    successRate: 96.7,
    usageCount: 34,
    lastRun: '4 hours ago',
    isPublic: true,
    rating: 4.6
  },
  {
    id: 'wf-def-004',
    name: 'Infrastructure Monitoring Setup',
    description: 'Automated deployment of monitoring stack with alerting',
    version: '3.0.1',
    status: 'published',
    category: 'monitoring',
    complexity: 'medium',
    createdAt: '2024-11-05T12:45:00Z',
    updatedAt: '2024-12-12T10:15:00Z',
    createdBy: 'Platform Team',
    tags: ['monitoring', 'alerting', 'prometheus', 'grafana'],
    stepCount: 10,
    averageExecutionTime: 300,
    successRate: 91.8,
    usageCount: 67,
    lastRun: '1 day ago',
    isPublic: true,
    rating: 4.5
  }
];

const mockExecutions: WorkflowExecution[] = [
  {
    id: 'exec-001',
    workflowId: 'wf-def-001',
    workflowName: 'CI/CD Pipeline Automation',
    status: 'completed',
    startTime: '2024-12-19T10:30:00Z',
    endTime: '2024-12-19T10:38:30Z',
    duration: 510,
    triggeredBy: 'GitHub Webhook',
    stepsCompleted: 12,
    totalSteps: 12,
    resourcesUsed: 4
  },
  {
    id: 'exec-002',
    workflowId: 'wf-def-002',
    workflowName: 'Data Lake ETL Processing',
    status: 'running',
    startTime: '2024-12-19T11:00:00Z',
    triggeredBy: 'Scheduled',
    stepsCompleted: 5,
    totalSteps: 8,
    resourcesUsed: 12
  },
  {
    id: 'exec-003',
    workflowId: 'wf-def-003',
    workflowName: 'Security Vulnerability Assessment',
    status: 'failed',
    startTime: '2024-12-19T09:15:00Z',
    endTime: '2024-12-19T09:18:45Z',
    duration: 225,
    triggeredBy: 'Manual',
    stepsCompleted: 4,
    totalSteps: 6,
    errorMessage: 'API rate limit exceeded',
    resourcesUsed: 2
  }
];

const mockTemplates: WorkflowTemplate[] = [
  {
    id: 'tpl-001',
    name: 'AWS EC2 Auto-Scaling',
    description: 'Template for setting up auto-scaling groups with load balancing',
    category: 'infrastructure',
    complexity: 'medium',
    tags: ['aws', 'auto-scaling', 'load-balancer'],
    usageCount: 245,
    rating: 4.7,
    preview: 'Auto-scaling configuration with CloudWatch integration',
    author: 'AWS Community',
    isPremium: false
  },
  {
    id: 'tpl-002',
    name: 'Kubernetes Blue-Green Deployment',
    description: 'Zero-downtime deployment strategy using Kubernetes',
    category: 'deployment',
    complexity: 'complex',
    tags: ['kubernetes', 'deployment', 'blue-green'],
    usageCount: 178,
    rating: 4.9,
    preview: 'Advanced deployment with rollback capabilities',
    author: 'K8s Experts',
    isPremium: true
  },
  {
    id: 'tpl-003',
    name: 'Database Backup & Recovery',
    description: 'Automated database backup with point-in-time recovery',
    category: 'data-processing',
    complexity: 'simple',
    tags: ['backup', 'database', 'recovery'],
    usageCount: 312,
    rating: 4.6,
    preview: 'Reliable backup solution with scheduling',
    author: 'DBA Tools',
    isPremium: false
  }
];

const mockMetrics: WorkflowMetrics = {
  totalWorkflows: 4,
  activeWorkflows: 3,
  executionsToday: 18,
  avgSuccessRate: 95.3,
  totalExecutionTime: 240,
  popularCategory: 'deployment'
};

// Chart data (unchanged)
const executionTrendData = [
  { date: '12/13', executions: 15, success: 14 },
  { date: '12/14', executions: 22, success: 20 },
  { date: '12/15', executions: 18, success: 17 },
  { date: '12/16', executions: 25, success: 24 },
  { date: '12/17', executions: 20, success: 19 },
  { date: '12/18', executions: 23, success: 22 },
  { date: '12/19', executions: 18, success: 17 }
];

const categoryUsageData = [
  { name: 'Deployment', value: 35, color: '#3b82f6' },
  { name: 'Data Processing', value: 28, color: '#10b981' },
  { name: 'Security', value: 20, color: '#f59e0b' },
  { name: 'Monitoring', value: 12, color: '#8b5cf6' },
  { name: 'Infrastructure', value: 5, color: '#ef4444' }
];

// -----------------------------------------------------------------------------
// Small helper functions (preserve original mapping logic)
// -----------------------------------------------------------------------------
const getStatusColor = (status: string) => {
  switch (status) {
    case 'published': return 'bg-green-100 text-green-800';
    case 'draft': return 'bg-yellow-100 text-yellow-800';
    case 'archived': return 'bg-gray-100 text-gray-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getComplexityColor = (complexity: string) => {
  switch (complexity) {
    case 'simple': return 'bg-blue-100 text-blue-800';
    case 'medium': return 'bg-yellow-100 text-yellow-800';
    case 'complex': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getExecutionStatusColor = (status: string) => {
  switch (status) {
    case 'completed': return 'bg-green-100 text-green-800';
    case 'running': return 'bg-blue-100 text-blue-800';
    case 'failed': return 'bg-red-100 text-red-800';
    case 'cancelled': return 'bg-gray-100 text-gray-800';
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getExecutionStatusIcon = (status: string) => {
  switch (status) {
    case 'completed': return <CheckCircle className="w-4 h-4" />;
    case 'running': return <Activity className="w-4 h-4" />;
    case 'failed': return <XCircle className="w-4 h-4" />;
    case 'cancelled': return <XCircle className="w-4 h-4" />;
    case 'pending': return <Clock className="w-4 h-4" />;
    default: return <Clock className="w-4 h-4" />;
  }
};

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'deployment': return <GitBranch className="w-4 h-4" />;
    case 'data-processing': return <Database className="w-4 h-4" />;
    case 'security': return <Shield className="w-4 h-4" />;
    case 'monitoring': return <BarChart3 className="w-4 h-4" />;
    case 'infrastructure': return <Server className="w-4 h-4" />;
    default: return <Workflow className="w-4 h-4" />;
  }
};

// -----------------------------------------------------------------------------
// Local UI sub-components (keeps file single but modular)
// - Keep logic identical to original, but improve spacing and readability.
// -----------------------------------------------------------------------------

const MetricsOverview: FC<{ metrics: WorkflowMetrics }> = ({ metrics }) => (
  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Total Workflows</CardTitle>
        <Workflow className="w-4 h-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{metrics.totalWorkflows}</div>
        <p className="text-xs text-muted-foreground">+2 this month</p>
      </CardContent>
    </Card>

    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Active</CardTitle>
        <Activity className="w-4 h-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{metrics.activeWorkflows}</div>
        <p className="text-xs text-muted-foreground">75% of total</p>
      </CardContent>
    </Card>

    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Executions Today</CardTitle>
        <Play className="w-4 h-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{metrics.executionsToday}</div>
        <p className="text-xs text-muted-foreground">+22% from yesterday</p>
      </CardContent>
    </Card>

    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
        <CheckCircle className="w-4 h-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{metrics.avgSuccessRate}%</div>
        <p className="text-xs text-muted-foreground">+1.2% improvement</p>
      </CardContent>
    </Card>

    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Avg Runtime</CardTitle>
        <Timer className="w-4 h-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{metrics.totalExecutionTime}m</div>
        <p className="text-xs text-muted-foreground">-8% optimization</p>
      </CardContent>
    </Card>

    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Popular Category</CardTitle>
        <TrendingUp className="w-4 h-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold capitalize">{metrics.popularCategory}</div>
        <p className="text-xs text-muted-foreground">35% of usage</p>
      </CardContent>
    </Card>
  </div>
);

// Filters card — small UI tweaks: clearer spacing and smaller select widths to avoid crowding
const FiltersCard: FC<{
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  categoryFilter: string;
  setCategoryFilter: (v: string) => void;
  statusFilter: string;
  setStatusFilter: (v: string) => void;
  complexityFilter: string;
  setComplexityFilter: (v: string) => void;
}> = ({ searchTerm, setSearchTerm, categoryFilter, setCategoryFilter, statusFilter, setStatusFilter, complexityFilter, setComplexityFilter }) => (
  <Card>
    <CardHeader>
      <CardTitle>Workflow Management</CardTitle>
      <CardDescription>Manage and monitor your workflow definitions</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:gap-4">
        <div className="flex-1">
          <Label>Search Workflows</Label>
          <Input
            placeholder="Search by name, description, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="min-w-[170px]">
          <Label>Category</Label>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="deployment">Deployment</SelectItem>
              <SelectItem value="data-processing">Data Processing</SelectItem>
              <SelectItem value="security">Security</SelectItem>
              <SelectItem value="monitoring">Monitoring</SelectItem>
              <SelectItem value="infrastructure">Infrastructure</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-[150px]">
          <Label>Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-[150px]">
          <Label>Complexity</Label>
          <Select value={complexityFilter} onValueChange={setComplexityFilter}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Complexity</SelectItem>
              <SelectItem value="simple">Simple</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="complex">Complex</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Workflows table component — UI spacing improved, logic preserved
const WorkflowsTable: FC<{ workflows: WorkflowDefinition[] }> = ({ workflows }) => (
  <Card>
    <CardContent className="p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Workflow</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Complexity</TableHead>
            <TableHead>Success Rate</TableHead>
            <TableHead>Usage</TableHead>
            <TableHead>Last Run</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {workflows.map((workflow) => (
            <TableRow key={workflow.id}>
              <TableCell>
                <div className="space-y-1">
                  <div className="flex items-center space-x-3">
                    <div className="font-medium">{workflow.name}</div>
                    {workflow.isPublic && <Badge variant="outline">Public</Badge>}
                    <div className="flex items-center space-x-1">
                      <Star className="w-3 h-3 text-yellow-500" />
                      <span className="text-xs text-muted-foreground">{workflow.rating}</span>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">{workflow.description}</div>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {workflow.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </TableCell>

              <TableCell>
                <Badge className={getStatusColor(workflow.status)}>
                  {workflow.status}
                </Badge>
              </TableCell>

              <TableCell>
                <div className="flex items-center space-x-2">
                  {getCategoryIcon(workflow.category)}
                  <span className="capitalize">{workflow.category.replace('-', ' ')}</span>
                </div>
              </TableCell>

              <TableCell>
                <Badge className={getComplexityColor(workflow.complexity)}>
                  {workflow.complexity}
                </Badge>
              </TableCell>

              <TableCell>
                <div className="space-y-1">
                  <div className="text-sm font-medium">{workflow.successRate}%</div>
                  <Progress value={workflow.successRate} className="w-16" />
                </div>
              </TableCell>

              <TableCell>
                <div className="text-sm">{workflow.usageCount} runs</div>
              </TableCell>

              <TableCell>
                <div className="text-sm">{workflow.lastRun || 'Never'}</div>
              </TableCell>

              <TableCell>
                <div className="flex items-center space-x-2">
                  <Button size="sm" variant="ghost">
                    <Play className="w-4 h-4" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuItem>
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Workflow
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Copy className="w-4 h-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Share className="w-4 h-4 mr-2" />
                        Share
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

// Executions table — spacing and preserved logic
const ExecutionsTable: FC<{ executions: WorkflowExecution[] }> = ({ executions }) => (
  <Card>
    <CardHeader>
      <CardTitle>Recent Executions</CardTitle>
      <CardDescription>Latest workflow execution history and status</CardDescription>
    </CardHeader>
    <CardContent>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Workflow</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Start Time</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Progress</TableHead>
            <TableHead>Triggered By</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {executions.map((execution) => (
            <TableRow key={execution.id}>
              <TableCell className="font-medium">{execution.workflowName}</TableCell>
              <TableCell>
                <div className="flex items-center space-x-2">
                  {getExecutionStatusIcon(execution.status)}
                  <Badge className={getExecutionStatusColor(execution.status)}>
                    {execution.status}
                  </Badge>
                </div>
              </TableCell>
              <TableCell>{new Date(execution.startTime).toLocaleString()}</TableCell>
              <TableCell>
                {execution.duration ? `${execution.duration}s` : 'Running...'}
              </TableCell>
              <TableCell>
                <div className="space-y-1">
                  <div className="text-sm">{execution.stepsCompleted}/{execution.totalSteps} steps</div>
                  <Progress 
                    value={(execution.stepsCompleted / execution.totalSteps) * 100} 
                    className="w-16" 
                  />
                </div>
              </TableCell>
              <TableCell>{execution.triggeredBy}</TableCell>
              <TableCell>
                <div className="flex items-center space-x-2">
                  <Button variant="ghost" size="sm">
                    <Eye className="w-4 h-4 mr-2" />
                    Logs
                  </Button>
                  {execution.status === 'running' && (
                    <Button variant="ghost" size="sm">
                      <Pause className="w-4 h-4 mr-2" />
                      Stop
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

// Templates grid — identical logic but tidier layout
const TemplatesGrid: FC<{ templates: WorkflowTemplate[] }> = ({ templates }) => (
  <Card>
    <CardHeader>
      <CardTitle>Workflow Templates</CardTitle>
      <CardDescription>Pre-built templates to jumpstart your workflows</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {templates.map((template) => (
          <Card key={template.id} className="hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{template.name}</CardTitle>
                {template.isPremium && (
                  <Badge variant="outline" className="text-yellow-600">Premium</Badge>
                )}
              </div>
              <CardDescription>{template.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span>Category:</span>
                  <Badge variant="secondary" className="capitalize">{template.category.replace('-', ' ')}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Complexity:</span>
                  <Badge className={getComplexityColor(template.complexity)}>{template.complexity}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Usage:</span>
                  <span>{template.usageCount} times</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Rating:</span>
                  <div className="flex items-center space-x-1">
                    <Star className="w-3 h-3 text-yellow-500" />
                    <span>{template.rating}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {template.tags.map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                  ))}
                </div>
                <div className="flex space-x-2 pt-2">
                  <Button size="sm" className="flex-1">
                    <Plus className="w-4 h-4 mr-1" />
                    Use Template
                  </Button>
                  <Button size="sm" variant="outline">
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </CardContent>
  </Card>
);

// Analytics panels: charts with identical data
const AnalyticsPanels: FC = () => (
  <div className="grid gap-4 md:grid-cols-2">
    <Card>
      <CardHeader>
        <CardTitle>Execution Trends</CardTitle>
        <CardDescription>Daily workflow executions over the last week</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={executionTrendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Line 
              type="monotone" 
              dataKey="executions" 
              stroke="#3b82f6" 
              strokeWidth={2}
              name="Total Executions"
            />
            <Line 
              type="monotone" 
              dataKey="success" 
              stroke="#10b981" 
              strokeWidth={2}
              name="Successful"
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle>Category Usage</CardTitle>
        <CardDescription>Workflow usage by category</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={categoryUsageData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {categoryUsageData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  </div>
);

// -----------------------------------------------------------------------------
// Main exported component — wiring everything together.
// Logic preserved exactly (filters, search, mapping functions etc.).
// -----------------------------------------------------------------------------
export default function WorkflowsPage() {
  // keep data states identical (immutable from outside)
  const [workflows] = useState<WorkflowDefinition[]>(mockWorkflows);
  const [filteredWorkflows, setFilteredWorkflows] = useState<WorkflowDefinition[]>(mockWorkflows);
  const [executions] = useState<WorkflowExecution[]>(mockExecutions);
  const [templates] = useState<WorkflowTemplate[]>(mockTemplates);
  const [metrics] = useState<WorkflowMetrics>(mockMetrics);

  // UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [complexityFilter, setComplexityFilter] = useState<string>("all");

  // Filtering logic: preserved
  useEffect(() => {
    let filtered = workflows;

    if (categoryFilter !== "all") {
      filtered = filtered.filter(wf => wf.category === categoryFilter);
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(wf => wf.status === statusFilter);
    }

    if (complexityFilter !== "all") {
      filtered = filtered.filter(wf => wf.complexity === complexityFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(wf =>
        wf.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        wf.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        wf.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredWorkflows(filtered);
  }, [workflows, categoryFilter, statusFilter, complexityFilter, searchTerm]);

  return (
    <div className="flex-1 space-y-6 p-6 md:p-8 pt-6">{/* increased general spacing */}
      <div className="flex items-center justify-between space-y-2">
        <h2 className="text-3xl font-bold tracking-tight">Workflows</h2>
        <div className="flex items-center space-x-3">{/* slightly larger gap */}
          <Button variant="outline">
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            Create Workflow
          </Button>
        </div>
      </div>

      {/* Metrics Overview */}
      <MetricsOverview metrics={metrics} />

      <Tabs defaultValue="workflows" className="space-y-4">
        <TabsList>
          <TabsTrigger value="workflows">My Workflows</TabsTrigger>
          <TabsTrigger value="executions">Executions</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="workflows" className="space-y-4">
          <FiltersCard
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            complexityFilter={complexityFilter}
            setComplexityFilter={setComplexityFilter}
          />

          <WorkflowsTable workflows={filteredWorkflows} />
        </TabsContent>

        <TabsContent value="executions" className="space-y-4">
          <ExecutionsTable executions={executions} />
        </TabsContent>

        <TabsContent value="templates" className="space-y-4">
          <TemplatesGrid templates={templates} />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <AnalyticsPanels />
        </TabsContent>
      </Tabs>
    </div>
  );
}
